import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import {
  TRoleSelectProps,
  TSystemRoles,
} from '@/shared/features/rbac/components/role-select/role-select.types';
import { useReadRolesForSelectQuery } from '@/shared/redux/api/ident/ident-api';
import {
  ReadRolesForSelectQuery,
  UserRoleForSelectFragment,
} from '@/shared/types/types.generated';

export const RoleSelect = <TFieldValues extends FieldValues, TOptionShape>({
  name,
  roleFilter,
  createOption,
  getItemId,
  disableTags,
  additionalOptions = [],
  ...rest
}: TRoleSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const createDefaultOption = (userRole: UserRoleForSelectFragment) => {
    return {
      label: userRole.name,
      value: userRole.userRoleId,
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadRolesForSelectQuery) => {
    if (!data) return additionalOptions;

    const { items } = data.readAllUserRoles;
    const roles = roleFilter ? items.filter(roleFilter) : items;
    const systemRoles = roles.filter(({ isSystemRole }) => isSystemRole);
    const customRoles = roles.filter(({ isSystemRole }) => !isSystemRole);

    const findSystemRole = (prefix: TSystemRoles) =>
      systemRoles.find(({ name }) => name.startsWith(prefix));

    const orderedSystemRoles = [
      findSystemRole('Admin'),
      findSystemRole('Engineer'),
      findSystemRole('Tech'),
      findSystemRole('Read Only'),
    ].filter(Boolean) as typeof systemRoles;

    const sortedCustomRoles = [...customRoles].sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    );

    return [
      ...additionalOptions,
      ...orderedSystemRoles.map(_createOption),
      ...sortedCustomRoles.map(_createOption),
    ];
  };

  return (
    <AsyncSearchableSelect<TFieldValues, ReadRolesForSelectQuery, TOptionShape>
      {...rest}
      name={name}
      label={t('rbac:roles')}
      placeholder={t('rbac:search_roles')}
      multiple
      required
      disableSort
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadRolesForSelectQuery,
        transformData,
      }}
      disableTags={disableTags}
    />
  );
};
